/**
 * Branding asset URL helper. Tenant branding (client/customer logos +
 * favicons) is served direct from the public-read branding bucket — see
 * `infra.md §18.1`. User avatars live in the same bucket but under a
 * private prefix; never resolve them through this helper.
 *
 * We resolve via the S3 virtual-hosted style URL rather than requiring the
 * backend to mint presigned GETs on every page load. Bucket region is
 * read from the `VITE_AWS_REGION` env (defaults to eu-west-1).
 *
 * Build-time guard: `VITE_BRANDING_BUCKET` must be set in the deployed
 * env. In local dev without it the helper returns null and the UI falls
 * back to the platform default.
 */

const BRANDING_BUCKET = import.meta.env.VITE_BRANDING_BUCKET as string | undefined;
const REGION = (import.meta.env.VITE_AWS_REGION as string | undefined) ?? "eu-west-1";

/**
 * Resolve a branding S3 key to a public URL with a cache-bust query param.
 *
 * P1.5-b (2026-04-22): when SuperAdmin impersonates a client, the logo /
 * favicon `<img src>` swaps to a new URL. Without a query-string suffix,
 * the browser HTTP cache can serve the *previous* tenant's image for
 * that URL (even for a completely different key, if the browser has
 * cached a 200 with a long max-age from S3). We append `?v={key}` so
 * every S3 key produces a distinct URL — browser cache becomes a
 * per-asset cache, not a per-path cache. The upload flow regenerates
 * the UUID in the key on every upload so `?v=...` also busts when a
 * tenant re-uploads their logo.
 */
export function brandingAssetUrl(s3Key: string | null | undefined): string | null {
  if (!s3Key) return null;
  // Only `client/*` and `customer/*` prefixes are publicly readable. Avatars
  // under `user-avatars/*` would 403 — return null so callers fall back.
  if (!s3Key.startsWith("client/") && !s3Key.startsWith("customer/")) return null;
  if (!BRANDING_BUCKET) return null;
  const base = `https://${BRANDING_BUCKET}.s3.${REGION}.amazonaws.com/${s3Key}`;
  // Cache-bust token derived from the key itself — stable while the key
  // is stable, changes the moment the key rotates (on re-upload).
  return `${base}?v=${encodeURIComponent(s3Key)}`;
}
